/**
 * React Query hooks for disk image operations.
 * Provides declarative data fetching with caching and automatic refetching.
 *
 * @module hooks/useDiskImages
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { diskImageService, ApiException } from '@/services';
import { showErrorToast } from '@/utils/errorToast';
import type {
  DiskImageRequest,
  DiskImageResponse,
  PublicDiskImageResponse,
  BuildDiskImageFromDockerfileRequest,
} from '@/types';

/**
 * Query key factory for disk images.
 * Ensures consistent cache key generation.
 */
export const diskImageKeys = {
  all: ['diskImages'] as const,
  lists: () => [...diskImageKeys.all, 'list'] as const,
  list: () => [...diskImageKeys.lists()] as const,
  details: () => [...diskImageKeys.all, 'detail'] as const,
  detail: (id: string) => [...diskImageKeys.details(), id] as const,
  public: ['publicDiskImages'] as const,
  publicList: () => [...diskImageKeys.public, 'list'] as const,
};

/**
 * Hook to fetch all disk images
 *
 * @example
 * const { data, isLoading, error } = useDiskImages();
 */
export function useDiskImages() {
  console.log('[useDiskImages] Hook called');
  return useQuery({
    queryKey: diskImageKeys.list(),
    queryFn: async () => {
      console.log('[useDiskImages] queryFn executing...');
      const result = await diskImageService.list();
      console.log('[useDiskImages] queryFn completed, result:', result);
      return result;
    },
  });
}

/**
 * Hook to fetch a single disk image by ID
 *
 * @param id - The disk image ID
 * @param sandboxGroupId - Optional sandbox group ID for group-scoped disk images
 * @example
 * const { data, isLoading, error } = useDiskImage('my-image-id');
 * const { data } = useDiskImage('my-image-id', 'group-id');
 */
export function useDiskImage(id: string, sandboxGroupId?: string) {
  return useQuery({
    queryKey: sandboxGroupId
      ? [...diskImageKeys.detail(id), 'group', sandboxGroupId]
      : diskImageKeys.detail(id),
    queryFn: () => diskImageService.getById(id, sandboxGroupId),
    enabled: !!id,
  });
}

/**
 * Hook to create a new disk image
 *
 * @example
 * const { mutate, isPending } = useCreateDiskImage();
 * mutate(diskImageData, { onSuccess: () => navigate('/disk-images') });
 */
export function useCreateDiskImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      request,
      sandboxGroupId,
    }: {
      request: DiskImageRequest;
      sandboxGroupId?: string;
    }) => diskImageService.create(request, sandboxGroupId),
    onSuccess: (data: DiskImageResponse) => {
      // Invalidate and refetch disk images list
      queryClient.invalidateQueries({ queryKey: diskImageKeys.lists() });
      // Pre-populate the detail cache
      queryClient.setQueryData(diskImageKeys.detail(data.id), data);
      toast.success(`Disk image "${data.labels?.['name'] || data.id}" created successfully`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to create disk image: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a disk image
 *
 * @example
 * const { mutate, isPending } = useDeleteDiskImage();
 * mutate('my-image-id');
 */
export function useDeleteDiskImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => diskImageService.delete(id),
    onSuccess: (_, id) => {
      // Invalidate the list cache
      queryClient.invalidateQueries({ queryKey: diskImageKeys.lists() });
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: diskImageKeys.detail(id) });
      toast.success(`Disk image deleted successfully`);
    },
    onError: (error: Error) => {
      // Use detailed message from API if available (e.g., 409 conflict with dependent images)
      const errorMessage =
        error instanceof ApiException && error.detail ? error.detail : error.message;
      showErrorToast(errorMessage, { duration: 6000 });
    },
  });
}

/**
 * Hook to fetch all public disk images
 *
 * @example
 * const { data, isLoading, error } = usePublicDiskImages();
 */
export function usePublicDiskImages() {
  return useQuery<PublicDiskImageResponse[]>({
    queryKey: diskImageKeys.publicList(),
    queryFn: () => diskImageService.listPublic(),
  });
}

/**
 * Hook to create a new public disk image (admin only)
 *
 * @example
 * const { mutate, isPending } = useCreatePublicDiskImage();
 * mutate(diskImageData, { onSuccess: () => navigate('/disk-images') });
 */
export function useCreatePublicDiskImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: DiskImageRequest) => diskImageService.createPublic(request),
    onSuccess: (data: DiskImageResponse) => {
      queryClient.invalidateQueries({ queryKey: diskImageKeys.publicList() });
      toast.success(`Public disk image "${data.labels?.['name'] || data.id}" created successfully`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to create public disk image: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a public disk image (admin only)
 *
 * @example
 * const { mutate, isPending } = useDeletePublicDiskImage();
 * mutate('my-image-id');
 */
export function useDeletePublicDiskImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => diskImageService.deletePublic(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: diskImageKeys.publicList() });
      toast.success(`Public disk image deleted successfully`);
    },
    onError: (error: Error) => {
      // Use detailed message from API if available (e.g., 409 conflict with dependent images)
      const errorMessage =
        error instanceof ApiException && error.detail ? error.detail : error.message;
      showErrorToast(errorMessage, { duration: 6000 });
    },
  });
}

/**
 * Hook to build a disk image from a Dockerfile
 *
 * @example
 * const { mutate, isPending } = useBuildDiskImageFromDockerfile();
 * mutate({ dockerfile: '...', labels: { name: 'my-image' } });
 */
export function useBuildDiskImageFromDockerfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      request,
      sandboxGroupId,
    }: {
      request: BuildDiskImageFromDockerfileRequest;
      sandboxGroupId?: string;
    }) => diskImageService.buildFromDockerfile(request, sandboxGroupId),
    onSuccess: (data: DiskImageResponse) => {
      // Invalidate and refetch disk images list
      queryClient.invalidateQueries({ queryKey: diskImageKeys.lists() });
      // Pre-populate the detail cache
      queryClient.setQueryData(diskImageKeys.detail(data.id), data);
      toast.success(`Disk image "${data.name || data.id}" build started successfully`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to build disk image from Dockerfile: ${error.message}`);
    },
  });
}
